import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../context/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { 
  Wind, Droplets, ThermometerSun, CloudRain, Sun, Cloud, CloudSnow,
  Navigation, Clock, CheckCircle, AlertTriangle, XCircle, Info, RefreshCw,
  Compass, Calendar, Leaf
} from 'lucide-react';
import { DepartmentNav } from '../components/DepartmentNav';
import { UpgradeGate } from '../components/UpgradeGate';
import { PageWhy } from '../components/PageWhy';
import axios from 'axios';
import { toast } from 'sonner';

import { API_URL } from '../config/api';

// Weather condition icon mapping
const getWeatherIcon = (condition) => {
  const icons = {
    'Clear': <Sun className="w-6 h-6 text-amber-500" />,
    'Clouds': <Cloud className="w-6 h-6 text-gray-400" />,
    'Rain': <CloudRain className="w-6 h-6 text-blue-500" />,
    'Drizzle': <CloudRain className="w-6 h-6 text-blue-400" />,
    'Thunderstorm': <CloudRain className="w-6 h-6 text-purple-500" />,
    'Snow': <CloudSnow className="w-6 h-6 text-blue-200" />,
    'Mist': <Cloud className="w-6 h-6 text-gray-300" />,
    'Fog': <Cloud className="w-6 h-6 text-gray-300" />
  };
  return icons[condition] || <Sun className="w-6 h-6 text-amber-500" />;
};

// Spray rating badge
const SprayRatingBadge = ({ rating }) => {
  const colors = {
    ideal: 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-400 border-emerald-200',
    good: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400 border-green-200',
    caution: 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-400 border-amber-200',
    poor: 'bg-orange-100 text-orange-800 dark:bg-orange-900/30 dark:text-orange-400 border-orange-200',
    no_spray: 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400 border-red-200'
  };
  
  const icons = {
    ideal: <CheckCircle className="w-3 h-3" />,
    good: <CheckCircle className="w-3 h-3" />,
    caution: <AlertTriangle className="w-3 h-3" />,
    poor: <AlertTriangle className="w-3 h-3" />,
    no_spray: <XCircle className="w-3 h-3" />
  };
  
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium border ${colors[rating.status]}`}>
      {icons[rating.status]}
      {rating.label}
    </span>
  );
};

// Wind direction arrow
const WindArrow = ({ degrees, size = 24 }) => (
  <div 
    className="inline-flex items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/30 p-1"
    style={{ transform: `rotate(${degrees}deg)` }}
  >
    <Navigation className={`w-${size/6} h-${size/6} text-blue-600`} style={{ width: size/2, height: size/2 }} />
  </div>
);

// Day forecast card
const DayCard = ({ day, isToday, isBestDay, onViewDetails }) => (
  <Card 
    className={`cursor-pointer transition-all hover:shadow-md ${
      isBestDay ? 'ring-2 ring-emerald-500 bg-emerald-50/50 dark:bg-emerald-950/20' : ''
    } ${isToday ? 'border-blue-500' : ''}`}
    onClick={() => onViewDetails(day)}
  >
    <CardContent className="p-4">
      {/* Header */}
      <div className="flex items-center justify-between mb-3">
        <div>
          <div className="font-semibold text-foreground">
            {isToday ? 'Today' : day.day_short}
          </div>
          <div className="text-xs text-muted-foreground">{day.month_day}</div>
        </div>
        <div className="flex items-center gap-2">
          {isBestDay && (
            <Badge className="bg-emerald-500 text-white text-xs">Best Day</Badge>
          )}
          {getWeatherIcon(day.conditions)}
        </div>
      </div>
      
      {/* Temps */}
      <div className="flex items-center gap-2 mb-3">
        <ThermometerSun className="w-4 h-4 text-orange-500" />
        <span className="font-medium">{day.temp_high}°</span>
        <span className="text-muted-foreground">{day.temp_low}°</span>
      </div>
      
      {/* Wind */}
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <Wind className="w-4 h-4 text-blue-500" />
          <span className="text-sm">{day.wind.avg_speed} mph</span>
        </div>
        <div className="flex items-center gap-1">
          <WindArrow degrees={day.wind.degrees} size={20} />
          <span className="text-xs text-muted-foreground">{day.wind.direction}</span>
        </div>
      </div>
      
      {/* Spray Rating */}
      <div className="flex items-center justify-between">
        <SprayRatingBadge rating={day.spray_rating} />
        {day.precipitation_chance > 0 && (
          <span className="text-xs text-blue-500 flex items-center gap-1">
            <Droplets className="w-3 h-3" />
            {day.precipitation_chance}%
          </span>
        )}
      </div>
      
      {/* GDD */}
      <div className="mt-2 pt-2 border-t flex items-center gap-1 text-xs text-muted-foreground">
        <Leaf className="w-3 h-3 text-emerald-500" />
        <span>+{day.gdd} GDD</span>
      </div>
    </CardContent>
  </Card>
);

export const SprayForecastPage = () => {
  const { user } = useAuth();
  const { t } = useTranslation(['common', 'nav', 'dashboard']);
  const [loading, setLoading] = useState(true);
  const [forecast, setForecast] = useState(null);
  const [selectedDay, setSelectedDay] = useState(null);
  const [showDayDetail, setShowDayDetail] = useState(false);

  useEffect(() => {
    fetchForecast();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchForecast = async () => {
    try {
      setLoading(true);
      const res = await axios.get(`${API_URL}/api/weather/spray-forecast`, { withCredentials: true });
      setForecast(res.data);
    } catch (error) {
      console.error('Failed to fetch spray forecast:', error);
      if (error.response?.status === 400) {
        toast.error('Please set your location first');
      } else {
        toast.error('Failed to load forecast');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleViewDetails = (day) => {
    setSelectedDay(day);
    setShowDayDetail(true);
  };

  const today = new Date().toISOString().split('T')[0];

  return (
    <div className="min-h-screen bg-background" data-testid="spray-forecast-page">
      <DepartmentNav />
      <UpgradeGate feature="spray_forecast" featureLabel="5-Day Spray Forecast">
      <main className="container mx-auto px-4 py-8 max-w-6xl">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl font-bold text-foreground flex items-center gap-3">
              <Wind className="w-8 h-8 text-blue-500" />
              Spray Forecast
            </h1>
            <p className="text-muted-foreground mt-1">
              5-day forecast with wind conditions for spray applications
            </p>
            <PageWhy testId="spray-forecast-page-why">Pick the right morning to spray — so your product lands on the lawn, not your neighbor's driveway.</PageWhy>
          </div>
          
          <Button onClick={fetchForecast} variant="outline" disabled={loading}>
            <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>

        {/* Weather Disclaimer */}
        <div className="flex items-start gap-2 p-3 rounded-lg bg-blue-50 dark:bg-blue-950/30 border border-blue-200 dark:border-blue-800 mb-6">
          <AlertTriangle className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
          <p className="text-sm text-blue-800 dark:text-blue-200">
            <strong>Weather data is for guidance only.</strong> Conditions can change rapidly. Always verify current conditions before spraying. 
            Wind speeds above 10 mph significantly increase drift risk. Follow all product label requirements for temperature, humidity, and wind restrictions.
          </p>
        </div>

        {loading ? (
          <div className="text-center py-12 text-muted-foreground">Loading forecast...</div>
        ) : !forecast ? (
          <Card>
            <CardContent className="py-12 text-center">
              <Wind className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
              <h3 className="text-lg font-medium mb-2">Set Your Location</h3>
              <p className="text-muted-foreground">
                Enable location or set your address in the dashboard to view the spray forecast.
              </p>
            </CardContent>
          </Card>
        ) : (
          <div className="space-y-6">
            {/* Best Spray Day Banner */}
            {forecast.best_spray_day && (
              <Card className="border-l-4 border-l-emerald-500 bg-emerald-50/50 dark:bg-emerald-950/20">
                <CardContent className="py-4">
                  <div className="flex items-center gap-3">
                    <CheckCircle className="w-6 h-6 text-emerald-500" />
                    <div>
                      <div className="font-semibold text-emerald-700 dark:text-emerald-400">
                        Best Day to Spray: {forecast.best_spray_day.day_name}
                      </div>
                      <div className="text-sm text-emerald-600 dark:text-emerald-500">
                        {forecast.best_spray_day.reason}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            )}

            {/* 5-Day Forecast Grid */}
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-5">
              {forecast.forecast?.map((day, idx) => (
                <DayCard 
                  key={day.date}
                  day={day}
                  isToday={day.date === today}
                  isBestDay={day.date === forecast.best_spray_day?.date}
                  onViewDetails={handleViewDetails}
                />
              ))}
            </div>

            {/* Spray Guidelines */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Info className="w-5 h-5 text-blue-500" />
                  Spray Guidelines
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-5">
                  <div className="flex items-center gap-2 p-2 rounded-lg bg-emerald-50 dark:bg-emerald-950/30">
                    <CheckCircle className="w-4 h-4 text-emerald-500" />
                    <div>
                      <div className="font-medium text-sm text-emerald-700 dark:text-emerald-400">Ideal</div>
                      <div className="text-xs text-emerald-600">0-3 mph</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 p-2 rounded-lg bg-green-50 dark:bg-green-950/30">
                    <CheckCircle className="w-4 h-4 text-green-500" />
                    <div>
                      <div className="font-medium text-sm text-green-700 dark:text-green-400">Good</div>
                      <div className="text-xs text-green-600">3-5 mph</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 p-2 rounded-lg bg-amber-50 dark:bg-amber-950/30">
                    <AlertTriangle className="w-4 h-4 text-amber-500" />
                    <div>
                      <div className="font-medium text-sm text-amber-700 dark:text-amber-400">Caution</div>
                      <div className="text-xs text-amber-600">5-10 mph</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 p-2 rounded-lg bg-orange-50 dark:bg-orange-950/30">
                    <AlertTriangle className="w-4 h-4 text-orange-500" />
                    <div>
                      <div className="font-medium text-sm text-orange-700 dark:text-orange-400">Poor</div>
                      <div className="text-xs text-orange-600">10-15 mph</div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2 p-2 rounded-lg bg-red-50 dark:bg-red-950/30">
                    <XCircle className="w-4 h-4 text-red-500" />
                    <div>
                      <div className="font-medium text-sm text-red-700 dark:text-red-400">No Spray</div>
                      <div className="text-xs text-red-600">15+ mph</div>
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Tips */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2 text-lg">
                  <Leaf className="w-5 h-5 text-emerald-500" />
                  Spray Application Tips
                </CardTitle>
              </CardHeader>
              <CardContent>
                <ul className="grid gap-2 sm:grid-cols-2">
                  {forecast.tips?.map((tip, idx) => (
                    <li key={idx} className="flex items-start gap-2 text-sm text-muted-foreground">
                      <CheckCircle className="w-4 h-4 text-emerald-500 mt-0.5 flex-shrink-0" />
                      {tip}
                    </li>
                  ))}
                </ul>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Day Detail Dialog */}
        <Dialog open={showDayDetail} onOpenChange={setShowDayDetail}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            {selectedDay && (
              <>
                <DialogHeader>
                  <DialogTitle className="flex items-center gap-3">
                    {getWeatherIcon(selectedDay.conditions)}
                    <span>{selectedDay.day_name}, {selectedDay.month_day}</span>
                    <SprayRatingBadge rating={selectedDay.spray_rating} />
                  </DialogTitle>
                </DialogHeader>
                
                <div className="space-y-6 mt-4">
                  {/* Summary */}
                  <div className="grid grid-cols-4 gap-4">
                    <div className="text-center p-3 rounded-lg bg-muted/50">
                      <ThermometerSun className="w-5 h-5 text-orange-500 mx-auto mb-1" />
                      <div className="font-bold">{selectedDay.temp_high}°/{selectedDay.temp_low}°</div>
                      <div className="text-xs text-muted-foreground">High/Low</div>
                    </div>
                    <div className="text-center p-3 rounded-lg bg-muted/50">
                      <Wind className="w-5 h-5 text-blue-500 mx-auto mb-1" />
                      <div className="font-bold">{selectedDay.wind.avg_speed} mph</div>
                      <div className="text-xs text-muted-foreground">Avg Wind</div>
                    </div>
                    <div className="text-center p-3 rounded-lg bg-muted/50">
                      <Compass className="w-5 h-5 text-indigo-500 mx-auto mb-1" />
                      <div className="font-bold">{selectedDay.wind.direction}</div>
                      <div className="text-xs text-muted-foreground">Direction</div>
                    </div>
                    <div className="text-center p-3 rounded-lg bg-muted/50">
                      <Droplets className="w-5 h-5 text-blue-400 mx-auto mb-1" />
                      <div className="font-bold">{selectedDay.precipitation_chance}%</div>
                      <div className="text-xs text-muted-foreground">Rain</div>
                    </div>
                  </div>

                  {/* Best Windows */}
                  <div>
                    <h4 className="font-medium mb-3 flex items-center gap-2">
                      <Clock className="w-4 h-4 text-emerald-500" />
                      Best Spray Windows
                    </h4>
                    <div className="flex gap-2 flex-wrap">
                      {selectedDay.best_spray_windows?.map((window, idx) => (
                        <div 
                          key={idx}
                          className="px-3 py-2 rounded-lg bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-200 dark:border-emerald-800"
                        >
                          <div className="font-medium text-emerald-700 dark:text-emerald-400">{window.time}</div>
                          <div className="text-xs text-emerald-600">{window.wind_speed} mph {window.wind_direction}</div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Hourly Breakdown */}
                  <div>
                    <h4 className="font-medium mb-3 flex items-center gap-2">
                      <Calendar className="w-4 h-4 text-blue-500" />
                      Hourly Conditions
                    </h4>
                    <div className="overflow-x-auto">
                      <div className="flex gap-2 pb-2">
                        {selectedDay.hourly?.map((hour, idx) => (
                          <div 
                            key={idx}
                            className={`flex-shrink-0 w-20 p-2 rounded-lg border text-center ${
                              hour.spray_rating.status === 'ideal' || hour.spray_rating.status === 'good'
                                ? 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200'
                                : hour.spray_rating.status === 'caution'
                                ? 'bg-amber-50 dark:bg-amber-950/30 border-amber-200'
                                : 'bg-red-50 dark:bg-red-950/30 border-red-200'
                            }`}
                          >
                            <div className="text-xs font-medium">{hour.time}</div>
                            <div className="text-lg font-bold">{hour.temp}°</div>
                            <div className="flex items-center justify-center gap-1">
                              <Wind className="w-3 h-3" />
                              <span className="text-xs">{hour.wind_speed}</span>
                            </div>
                            <div className="text-xs text-muted-foreground">{hour.wind_direction}</div>
                          </div>
                        ))}
                      </div>
                    </div>
                  </div>

                  {/* Spray Message */}
                  <div className={`p-4 rounded-lg border ${
                    selectedDay.spray_rating.status === 'ideal' || selectedDay.spray_rating.status === 'good'
                      ? 'bg-emerald-50 dark:bg-emerald-950/30 border-emerald-200'
                      : selectedDay.spray_rating.status === 'caution'
                      ? 'bg-amber-50 dark:bg-amber-950/30 border-amber-200'
                      : 'bg-red-50 dark:bg-red-950/30 border-red-200'
                  }`}>
                    <p className="text-sm">{selectedDay.spray_rating.message}</p>
                  </div>
                </div>
              </>
            )}
          </DialogContent>
        </Dialog>
      </main>
      </UpgradeGate>
    </div>
  );
};

export default SprayForecastPage;
